import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Info, Landmark } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../../components/ui/input-otp";
import {
  createPlaidBankLinkToken,
  getPlaidBankVerificationStatus,
  forgottonPassword,
  getPaymentdetailsByUserid,
  verifyEmailByOtpGiftCard,
} from "../../../reducers/authentication.slice";
import {
  createWithdrawalRequest,
} from "../../../reducers/giftcard.slice";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { BankLogo } from "../../../assests/logo/BankLogo";
import { CoinDollarLogo } from "../../../assests/logo/CoinsDollor";
import PaypalLogo from "../../../assests/images/Paypal.png";
import PageHeader from "../../../components/shared/PageHeader";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const PLAID_BANK_VERIFICATION_POLL_INTERVAL_MS = 2000;
const PLAID_BANK_VERIFICATION_MAX_ATTEMPTS = 30;
const PLAID_BANK_VERIFICATION_TERMINAL_STATUSES = [
  "VERIFIED",
  "FAILED",
  "EXITED",
  "ERROR",
];

function maskSensitiveNumber(number) {
  if (!number) return "****";
  const lastFour = number.slice(-4);
  return `${"*".repeat(Math.max(0, number.length - 4))}${lastFour}`;
}

const WithdrawPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { method } = useParams();
  const { showToast, walletSummary } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const user = getUsers();
  const isBank = method === "bank-account";

  const [showLoader, setShowLoader] = useState(false);
  const [formData, setFormData] = useState({ amount: "" });
  const [open, setOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = useState("");
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [paypalAccount, setPaypalAccount] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [bankVerificationError, setBankVerificationError] = useState("");
  const [pendingPlaidBankVerificationId, setPendingPlaidBankVerificationId] =
    useState(null);
  const plaidBankVerificationPollingRef = useRef(null);
  const plaidBankVerificationPollingAccountRef = useRef(null);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (showLoader) {
        event.preventDefault();
        event.returnValue = "API is still processing. Please wait.";
      }
    };
    const blockRefresh = (event) => {
      if (
        showLoader &&
        (event.key === "F5" || (event.ctrlKey && event.key === "r"))
      ) {
        event.preventDefault();
        showToast("API is still processing. Please wait.", "warning");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", blockRefresh);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", blockRefresh);
    };
  }, [showLoader]);

  const loadPaymentDetails = async (paymentMethodIdToSelect = null) => {
    const res = await dispatch(getPaymentdetailsByUserid());
    if (res?.error && Object.keys(res?.error)?.length > 0) {
      showToast(res?.error?.message, "error");
      return;
    }
    const filteredPayPalAccount = [];
    const filteredBankAccount = [];
    res?.payload?.forEach((item) => {
      if (item?.payment_method === "PayPal") filteredPayPalAccount.push(item);
      if (item?.payment_method === "ACH") filteredBankAccount.push(item);
    });
    setPaypalAccount(filteredPayPalAccount);
    setPaymentDetails(filteredBankAccount);
    if (paymentMethodIdToSelect) {
      const refreshedAccount = filteredBankAccount.find(
        (item) => item.id === paymentMethodIdToSelect
      );
      if (refreshedAccount) setSelectedAccount(refreshedAccount);
    }
  };

  useEffect(() => {
    setShowLoader(true);
    loadPaymentDetails().finally(() => setShowLoader(false));
  }, []);

  const stopPlaidBankVerificationPolling = () => {
    if (plaidBankVerificationPollingRef.current) {
      clearTimeout(plaidBankVerificationPollingRef.current);
      plaidBankVerificationPollingRef.current = null;
    }
    plaidBankVerificationPollingAccountRef.current = null;
  };

  useEffect(() => {
    return () => {
      stopPlaidBankVerificationPolling();
    };
  }, []);

  const checkPlaidBankVerificationStatus = async (
    paymentMethodId,
    options = {}
  ) => {
    const { showProcessingToast = true } = options;
    const response = await dispatch(
      getPlaidBankVerificationStatus({ payment_method_id: paymentMethodId })
    );
    if (response?.error && Object.keys(response?.error)?.length > 0) {
      showToast(response?.error?.message, "error");
      setBankVerificationError(response?.error?.message);
      setPendingPlaidBankVerificationId(null);
      return "ERROR";
    }
    const verificationDetails = response?.payload;
    if (verificationDetails?.is_plaid_bank_verified) {
      await loadPaymentDetails(paymentMethodId);
      showToast("Bank account verified successfully", "success");
      setBankVerificationError("");
      setPendingPlaidBankVerificationId(null);
      return "VERIFIED";
    }
    if (verificationDetails?.status === "FAILED") {
      await loadPaymentDetails(paymentMethodId);
      const errorMessage =
        verificationDetails?.error_message || "Bank verification failed";
      showToast(errorMessage, "error");
      setBankVerificationError(errorMessage);
      setPendingPlaidBankVerificationId(null);
      return "FAILED";
    }
    if (verificationDetails?.status === "EXITED") {
      await loadPaymentDetails(paymentMethodId);
      showToast("Bank verification was not completed", "warning");
      setBankVerificationError("Bank verification was not completed");
      setPendingPlaidBankVerificationId(null);
      return "EXITED";
    }
    if (showProcessingToast)
      showToast("Bank verification is still processing", "info");
    return verificationDetails?.status || "PROCESSING";
  };

  const pollPlaidBankVerificationStatus = (paymentMethodId) => {
    if (!paymentMethodId) return;
    if (plaidBankVerificationPollingAccountRef.current === paymentMethodId)
      return;
    stopPlaidBankVerificationPolling();
    setPendingPlaidBankVerificationId(paymentMethodId);

    const pollStatus = async (attempt = 1) => {
      plaidBankVerificationPollingAccountRef.current = paymentMethodId;
      const status = await checkPlaidBankVerificationStatus(paymentMethodId, {
        showProcessingToast: attempt === 1,
      });
      if (
        plaidBankVerificationPollingAccountRef.current !== paymentMethodId ||
        PLAID_BANK_VERIFICATION_TERMINAL_STATUSES.includes(status)
      ) {
        stopPlaidBankVerificationPolling();
        return;
      }
      if (attempt >= PLAID_BANK_VERIFICATION_MAX_ATTEMPTS) {
        stopPlaidBankVerificationPolling();
        showToast(
          "Bank verification is still processing. Please check again in a moment.",
          "info"
        );
        return;
      }
      plaidBankVerificationPollingRef.current = setTimeout(
        () => pollStatus(attempt + 1),
        PLAID_BANK_VERIFICATION_POLL_INTERVAL_MS
      );
    };
    pollStatus();
  };

  useEffect(() => {
    const refreshPaymentDetails = () => {
      if (method !== "bank-account" || !selectedAccount?.id) return;
      if (document.visibilityState !== "visible") return;
      if (pendingPlaidBankVerificationId) {
        pollPlaidBankVerificationStatus(pendingPlaidBankVerificationId);
        return;
      }
      loadPaymentDetails(selectedAccount.id);
    };
    window.addEventListener("focus", refreshPaymentDetails);
    document.addEventListener("visibilitychange", refreshPaymentDetails);
    return () => {
      window.removeEventListener("focus", refreshPaymentDetails);
      document.removeEventListener("visibilitychange", refreshPaymentDetails);
    };
  }, [method, selectedAccount?.id, pendingPlaidBankVerificationId]);

  const requiresBankVerification =
    isBank &&
    selectedAccount &&
    selectedAccount.is_plaid_bank_verified !== true;

  const openPlaidBankVerificationUrl = (hostedLinkUrl) => {
    if (!hostedLinkUrl) {
      showToast("Bank verification link is not available yet.", "error");
      return;
    }
    window.open(hostedLinkUrl, "_blank", "noopener,noreferrer");
  };

  const handleCreateBankLinkToken = async () => {
    if (!formData.amount || formData.amount <= 0 || selectedAccount === null) {
      showToast("Please fill all the fields", "error");
      return;
    }
    if (formData.amount > walletSummary?.current_balance) {
      showToast("Amount exceeds your current balance", "error");
      return;
    }
    setShowLoader(true);
    const response = await dispatch(
      createPlaidBankLinkToken({ payment_method_id: selectedAccount.id })
    );
    if (response?.error && Object.keys(response?.error)?.length > 0) {
      showToast(response?.error?.message, "error");
      setShowLoader(false);
      return;
    }
    setBankVerificationError("");
    setPendingPlaidBankVerificationId(selectedAccount.id);
    showToast("Complete bank verification in the Plaid tab.", "info");
    openPlaidBankVerificationUrl(response?.payload?.hosted_link_url);
    pollPlaidBankVerificationStatus(selectedAccount.id);
    setShowLoader(false);
  };

  const handlePrimaryButtonClick = () => {
    if (requiresBankVerification) {
      handleCreateBankLinkToken();
      return;
    }
    handleSendOtp();
  };

  const handleSendOtp = () => {
    if (!formData.amount || formData.amount <= 0 || selectedAccount === null) {
      showToast("Please fill all the fields", "error");
      return;
    }
    setShowLoader(true);
    const payload = {
      email: user?.email,
      email_type: "WITHDRAWAL_OTP",
    };
    dispatch(forgottonPassword(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      setOpen(true);
      showToast("Verification code has been successfully sent", "success");
      setShowLoader(false);
    });
  };

  const handleVerifyCode = async (event) => {
    setShowLoader(true);
    event.preventDefault();
    if (verificationCode.length === 6) {
      const payload = {
        otp: verificationCode,
        payment_method_id: parseFloat(selectedAccount?.id),
        amount: formData.amount,
        status: "PENDING",
      };
      const otpPayload = {
        id: user?.id,
        otp: verificationCode,
      };
      await dispatch(verifyEmailByOtpGiftCard(otpPayload)).then(async (res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setCodeError(true);
          setShowLoader(false);
          setCodeErrorMessage(res?.error?.message);
          return;
        }
        await dispatch(createWithdrawalRequest(payload)).then(async (respe) => {
          if (respe?.error && Object.keys(respe?.error)?.length > 0) {
            showToast(respe?.error?.message, "error");
            handleClose();
            setShowLoader(false);
            return;
          }
          showToast("Withdrawal request submitted successfully.", "success");
          handleClose();
          setShowLoader(false);
          navigate("/dashboard/wallet");
        });
      });
    } else {
      setCodeError(true);
      setShowLoader(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setVerificationCode("");
    setCodeError(false);
  };

  const accountOptions = isBank ? paymentDetails : paypalAccount;
  const isSubmitDisabled =
    !formData.amount ||
    selectedAccount === null ||
    formData.amount <= 0 ||
    formData.amount > walletSummary?.current_balance ||
    showLoader;

  return (
    <div style={{ maxWidth: 640, width: "100%", margin: "0 auto" }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          marginBottom: 20,
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <div
        style={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 18,
          marginBottom: 24,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isBank
              ? "linear-gradient(135deg, rgba(80,42,19,1), rgba(6,5,14,1))"
              : "linear-gradient(135deg, rgba(1,13,131,1), rgba(0,5,49,1))",
            border: `1px solid ${isBank ? "rgba(72,39,19,1)" : "rgba(1,12,129,1)"}`,
          }}
        >
          {isBank ? (
            <BankLogo height={30} width={30} />
          ) : (
            <CoinDollarLogo height={30} width={30} />
          )}
        </div>
        <PageHeader
          title={isBank ? "Withdraw to Bank Account" : "Withdraw to PayPal"}
          subtitle="Enter the amount and select the account where the money should be deposited."
        />
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 18,
          padding: isMobile ? 18 : 28,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        }}
      >
        <div>
          <Label htmlFor="amount">Amount</Label>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#c3c8cc",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              $
            </span>
            <Input
              id="amount"
              type="text"
              inputMode="decimal"
              placeholder="Enter amount"
              value={formData.amount}
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (e.key === "-" || e.key === "e" || e.key === "E")
                  e.preventDefault();
              }}
              onChange={(e) => {
                const value = e.target.value
                  .replace(/[^\d.]/g, "")
                  .replace(/(\..*)\./g, "$1");
                if (parseFloat(value) < 0) return;
                setFormData({ ...formData, amount: value });
              }}
              style={{
                paddingLeft: 30,
                border:
                  formData.amount > walletSummary?.current_balance
                    ? "1px solid #f27059"
                    : undefined,
              }}
            />
          </div>
          {formData.amount > walletSummary?.current_balance && (
            <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>
              Amount exceeds your current balance of $
              {walletSummary?.current_balance}
            </div>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <Label>Select Account</Label>
          {showLoader ? (
            <div style={{ fontSize: 12.5, color: "#c3c8cc", padding: "10px 0" }}>
              Loading accounts…
            </div>
          ) : accountOptions.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "#c3c8cc", padding: "10px 0" }}>
              No {isBank ? "bank" : "PayPal"} accounts on file yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {accountOptions.map((account) => {
                const selected = selectedAccount?.id === account.id;
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => {
                      stopPlaidBankVerificationPolling();
                      setBankVerificationError("");
                      setPendingPlaidBankVerificationId(null);
                      setSelectedAccount(account);
                    }}
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexDirection: isMobile ? "column" : "row",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: `1px solid ${selected ? "rgba(0,173,181,0.4)" : "rgba(255,255,255,0.08)"}`,
                      background: selected
                        ? "rgba(0,173,181,0.07)"
                        : "rgba(255,255,255,0.015)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        minWidth: 0,
                        width: isMobile ? "100%" : "auto",
                      }}
                    >
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isBank ? (
                          <Landmark size={16} color="#c3c8cc" />
                        ) : (
                          <img
                            src={PaypalLogo}
                            alt="PayPal"
                            style={{ width: 18, height: 18, objectFit: "contain" }}
                          />
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          {isBank
                            ? account.account_last_four ? `****${account.account_last_four}` : maskSensitiveNumber(account.account_number)
                            : account.paypal_username || account.email}
                        </div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "#c3c8cc",
                            marginTop: 2,
                          }}
                        >
                          {account.account_holder_name}
                          {isBank && (
                            <span
                              style={{
                                color: account.is_plaid_bank_verified
                                  ? "#4caf50"
                                  : "#c3c8cc",
                              }}
                            >
                              {" · "}
                              {account.is_plaid_bank_verified
                                ? "Plaid-verified"
                                : "Not Plaid-verified"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                        alignSelf: isMobile ? "flex-end" : "center",
                      }}
                    >
                      {account.is_primary && (
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: "#4caf50",
                            background: "rgba(76,175,80,0.15)",
                            padding: "2px 8px",
                            borderRadius: 999,
                          }}
                        >
                          Primary
                        </span>
                      )}
                      {selected && (
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            background: "rgba(0,173,181,0.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Check size={12} color="#00ADB5" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {isBank && bankVerificationError && (
            <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 8 }}>
              {bankVerificationError}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate("/dashboard/wallet/payment-methods")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 16,
            background: "transparent",
            border: "none",
            color: "#2196F3",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            padding: 0,
          }}
        >
          Want to add a new {isBank ? "bank account" : "PayPal account"}? Add
          new Account
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 24,
          }}
        >
          <Button
            onClick={handlePrimaryButtonClick}
            disabled={isSubmitDisabled}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            {requiresBankVerification
              ? "Verify Bank Details"
              : isBank
              ? "Submit"
              : "Withdraw"}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent style={{ maxWidth: 440 }}>
          <DialogHeader>
            <DialogTitle>Confirm Details</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: 13, color: "#c3c8cc", lineHeight: 1.6 }}>
            Confirm the payment method and the amount you have submitted for
            withdrawal. This cannot be undone once confirmed.
          </p>
          <div
            style={{
              background: "rgba(255,255,255,0.04)",
              borderRadius: 10,
              padding: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: "#c3c8cc",
                gap: 12,
              }}
            >
              <span>Selected Account</span>
              <span>
                {(isBank
                  ? selectedAccount?.account_last_four ? `****${selectedAccount.account_last_four}` : maskSensitiveNumber(selectedAccount?.account_number)
                  : selectedAccount?.paypal_username ||
                    selectedAccount?.email) || "N/A"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: "#fff",
                marginTop: 10,
                gap: 12,
              }}
            >
              <span>Amount</span>
              <span>
                {formData.amount ? `$${formData.amount}` : "N/A"}
              </span>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Label>Verification Code</Label>
            <InputOTP
              maxLength={6}
              value={verificationCode}
              onChange={(v) => {
                setVerificationCode(v);
                setCodeError(false);
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              style={{ marginBottom: 10 }}
            >
              <InputOTPGroup>
                {Array.from({ length: 6 }).map((_, i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    hasError={codeError}
                    style={{ marginRight: i < 5 ? 6 : 0 }}
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
            {codeError && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#f27059",
                  marginBottom: 8,
                }}
              >
                {codeErrorMessage || "Please enter a valid verification code"}
              </div>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                fontSize: 12,
                color: "#c3c8cc",
                lineHeight: 1.5,
              }}
            >
              <Info size={13} /> We've sent a verification code to your email:{" "}
              {user?.email}
            </div>
          </div>

          <DialogFooter style={{ marginTop: 16 }}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={showLoader}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || showLoader}
            >
              {showLoader ? "Verifying…" : "Verify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WithdrawPage;
